import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid,
  Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  Chip, Divider, Avatar, useMediaQuery, useTheme,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon, QrCode as QrIcon,
  CheckCircle as CheckIcon, LocationOn as LocationIcon,
} from '@mui/icons-material';
import { patientAPI, locationAPI } from '../services/api';
import { maskName, maskMobile } from '../utils/mask';
import toast from 'react-hot-toast';

function PatientRegister() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    name: '', mobile: '', dateOfBirth: '', gender: '', locationId: '', aadhaarNumber: '',
  });
  const [aadhaarError, setAadhaarError] = useState('');
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    locationAPI.getAll().then(res => {
      const locs = res.data.data || [];
      setLocations(locs);
      if (locs.length === 1) setForm(f => ({ ...f, locationId: locs[0]._id }));
    }).catch(() => {});
  }, []);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (success) setSuccess(null);
    if (field === 'aadhaarNumber') setAadhaarError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error('Name and mobile are required');
      return;
    }
    if (form.aadhaarNumber && !/^\d{12}$/.test(form.aadhaarNumber.replace(/\s/g, ''))) {
      setAadhaarError('Aadhaar number must be exactly 12 digits');
      return;
    }
    try {
      setLoading(true);
      const payload = { ...form, aadhaarNumber: form.aadhaarNumber.replace(/\s/g, '') || undefined };
      const res = await patientAPI.register(payload);
      setSuccess(res.data.data);
      toast.success('Patient registered successfully!');
      setForm({ name: '', mobile: '', dateOfBirth: '', gender: '', locationId: form.locationId, aadhaarNumber: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant={isSmall ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: '#0f172a' }}>
          Register Patient
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Register a new patient and generate their QR code for station check-in
        </Typography>
      </Box>

      {/* Success Card */}
      {success && (
        <Card sx={{
          mb: 3, overflow: 'hidden',
          border: '2px solid #10b981',
          background: 'linear-gradient(135deg, #ecfdf5, #f0fdfa)',
        }}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
            <Avatar sx={{
              width: 56, height: 56, mx: 'auto', mb: 2,
              bgcolor: '#10b981',
              boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
            }}>
              <CheckIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#065f46', mb: 1 }}>
              Patient Registered Successfully!
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
              <Chip
                icon={<QrIcon />}
                label={success.patient?.qrCode}
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1.1rem' },
                  fontFamily: 'monospace', fontWeight: 700, py: 2.5, px: 1,
                  bgcolor: '#fff', border: '2px solid #10b981',
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {maskName(success.patient?.name)} &middot; {maskMobile(success.patient?.mobile)}
            </Typography>
            {success.session && (
              <Typography variant="body2" sx={{ mt: 1, color: '#059669', fontWeight: 500 }}>
                Test session created with {success.session.totalStations} stations
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Registration Form */}
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Avatar sx={{
              width: 40, height: 40,
              background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              boxShadow: '0 4px 12px rgba(8,145,178,0.3)',
            }}>
              <PersonAddIcon />
            </Avatar>
            <Typography variant="h6" fontWeight={700} color="#0f172a">Patient Information</Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth required label="Full Name" value={form.name}
                  onChange={handleChange('name')} placeholder="Enter patient's full name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth required label="Mobile Number" value={form.mobile}
                  onChange={handleChange('mobile')} placeholder="10-digit mobile number"
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Date of Birth" type="date" value={form.dateOfBirth}
                  onChange={handleChange('dateOfBirth')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select value={form.gender} label="Gender" onChange={handleChange('gender')}>
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Aadhaar Number (optional)"
                  value={form.aadhaarNumber}
                  onChange={handleChange('aadhaarNumber')}
                  placeholder="12-digit Aadhaar number"
                  inputProps={{ maxLength: 12, inputMode: 'numeric' }}
                  error={!!aadhaarError}
                  helperText={aadhaarError || 'Optional — links patient for biometric login'}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LocationIcon sx={{ color: '#0891b2' }} />
                  <Typography variant="subtitle2" fontWeight={600} color="#0f172a">Camp Location</Typography>
                </Box>
                <FormControl fullWidth>
                  <InputLabel>Select Location</InputLabel>
                  <Select value={form.locationId} label="Select Location" onChange={handleChange('locationId')}>
                    <MenuItem value="">No Location (register only)</MenuItem>
                    {locations.map(loc => (
                      <MenuItem key={loc._id} value={loc._id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`${loc.stationCount || loc.numStations} stations`}
                            size="small" variant="outlined" sx={{ fontSize: '0.7rem' }}
                          />
                          {loc.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {form.locationId && (
                  <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                    A test session will be auto-created for the selected location's stations.
                    The patient will receive a QR code for check-in.
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit" variant="contained" size="large" fullWidth
                  disabled={loading || !form.name.trim() || !form.mobile.trim()}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                  sx={{ py: 1.5, fontSize: '1rem', borderRadius: 3 }}
                >
                  {loading ? 'Registering...' : 'Register Patient'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}

export default PatientRegister;
